import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Shield,
  AlertTriangle,
  Home,
  Users,
  Heart,
  Map,
  Zap,
  Trophy,
  BookOpen,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Flame,
  Target,
  Brain,
  Crown
} from 'lucide-react'

/**
 * OnboardingScreen Component
 * Interactive onboarding flow for new users
 * Enhanced with brand colors, glassmorphism, and micro-animations
 */
const OnboardingScreen = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedOption, setSelectedOption] = useState(null)
  const [userAnswers, setUserAnswers] = useState({
    preparednessLevel: null,
    primaryConcern: null,
    familySize: null,
    learningStyle: null
  })

  const onboardingSteps = [
    {
      id: 'welcome',
      type: 'hero',
      label: 'Welcome',
      title: 'Welcome to SurviveIt',
      subtitle: 'Your Journey to Preparedness Starts Here',
      description: 'In the next 2 minutes, we\'ll personalize your training based on your needs and concerns.',
      icon: Shield
    },
    {
      id: 'assessment',
      type: 'question',
      label: 'Level',
      title: 'How Prepared Are You?',
      subtitle: 'Be honest - we\'ll meet you where you are',
      icon: Target,
      question: 'Rate your current emergency preparedness',
      options: [
        { value: 'beginner', label: 'Beginner', emoji: '🌱', description: 'Just starting my journey' },
        { value: 'intermediate', label: 'Learning', emoji: '📚', description: 'Know some basics' },
        { value: 'advanced', label: 'Prepared', emoji: '🛡️', description: 'Have plans in place' },
        { value: 'expert', label: 'Expert', emoji: '⭐', description: 'Ready for anything' }
      ],
      answerKey: 'preparednessLevel'
    },
    {
      id: 'concerns',
      type: 'question',
      label: 'Concerns',
      title: 'What Worries You Most?',
      subtitle: 'We\'ll prioritize content for your top concern',
      icon: AlertTriangle,
      question: 'Select your primary emergency concern',
      options: [
        { value: 'natural', label: 'Natural Disasters', emoji: '🌪️', description: 'Storms, earthquakes, floods' },
        { value: 'home', label: 'Home Emergencies', emoji: '🏠', description: 'Fire, outages, accidents' },
        { value: 'outdoor', label: 'Wilderness', emoji: '🏔️', description: 'Lost, injured, stranded' },
        { value: 'social', label: 'Civil Crises', emoji: '🚨', description: 'Unrest, evacuations' }
      ],
      answerKey: 'primaryConcern'
    },
    {
      id: 'family',
      type: 'question',
      label: 'Family',
      title: 'Who Are You Protecting?',
      subtitle: 'Larger households need different strategies',
      icon: Users,
      question: 'How many people in your household?',
      options: [
        { value: 'solo', label: 'Just Me', emoji: '👤', description: 'Solo preparedness' },
        { value: 'couple', label: 'Two of Us', emoji: '👥', description: 'Partner or roommate' },
        { value: 'small-family', label: 'Small Family', emoji: '👨‍👩‍👧', description: '3-4 people' },
        { value: 'large-family', label: 'Large Family', emoji: '👨‍👩‍👧‍👦', description: '5+ people' }
      ],
      answerKey: 'familySize'
    },
    {
      id: 'learning',
      type: 'question',
      label: 'Style',
      title: 'How Do You Learn Best?',
      subtitle: 'We\'ll adapt the content to your style',
      icon: Brain,
      question: 'Choose your preferred learning method',
      options: [
        { value: 'visual', label: 'Visual', emoji: '🎬', description: 'Videos & infographics' },
        { value: 'hands-on', label: 'Interactive', emoji: '🎮', description: 'Simulations & practice' },
        { value: 'reading', label: 'Reading', emoji: '📖', description: 'Detailed guides' },
        { value: 'challenges', label: 'Challenges', emoji: '🏆', description: 'Gamified learning' }
      ],
      answerKey: 'learningStyle'
    },
    {
      id: 'features',
      type: 'showcase',
      label: 'Features',
      title: 'Your Personalized Plan',
      subtitle: 'Based on your answers, here\'s what awaits:',
      icon: Sparkles,
      features: [
        {
          icon: BookOpen,
          title: '250+ Lessons',
          description: 'Tailored to your concerns across 5 disaster paths',
          color: '#f97316'
        },
        {
          icon: Zap,
          title: 'Emergency Mode',
          description: 'Instant access to critical info when seconds count',
          color: '#dc2626'
        },
        {
          icon: Users,
          title: 'Family Sync',
          description: 'Keep everyone aligned with shared plans & challenges',
          color: '#8b5cf6'
        },
        {
          icon: Trophy,
          title: 'Gamified XP',
          description: 'Stay motivated with badges, streaks & leaderboards',
          color: '#10b981'
        }
      ]
    },
    {
      id: 'ready',
      type: 'cta',
      label: 'Start',
      title: 'Ready to Begin?',
      subtitle: 'Join thousands of prepared families',
      description: 'Create your free account and start your first lesson in 60 seconds.',
      icon: Flame,
      benefits: [
        'Free tier with 50 lessons',
        'No credit card required',
        'Emergency Mode always free',
        'Cancel premium anytime'
      ]
    }
  ]

  const currentStepData = onboardingSteps[currentStep]

  const handleAnswer = (value) => {
    setSelectedOption(value)
    if (currentStepData.answerKey) {
      setUserAnswers(prev => ({
        ...prev,
        [currentStepData.answerKey]: value
      }))
    }
    // Delay to show selection animation
    setTimeout(() => {
      setSelectedOption(null)
      handleNext()
    }, 300)
  }

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      localStorage.setItem('onboardingAnswers', JSON.stringify(userAnswers))
      navigate('/auth?from=onboarding')
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = () => {
    navigate('/auth')
  }

  const renderStep = () => {
    const Icon = currentStepData.icon

    switch (currentStepData.type) {
      case 'hero':
        return (
          <motion.div
            key={currentStepData.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center max-w-2xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex p-8 rounded-3xl bg-gradient-to-br from-orange-500/20 to-red-500/20 mb-8 relative"
            >
              <motion.div
                animate={{
                  boxShadow: ['0 0 20px rgba(249,115,22,0.3)', '0 0 40px rgba(249,115,22,0.5)', '0 0 20px rgba(249,115,22,0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 rounded-3xl"
              />
              <Icon className="w-20 h-20 text-orange-500" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-5xl md:text-6xl font-bold text-white mb-4"
            >
              {currentStepData.title.split(' ').map((word, i) => (
                <span key={i} className={word === 'SurviveIt' ? 'bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent' : ''}>
                  {word}{' '}
                </span>
              ))}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-orange-100 mb-4"
            >
              {currentStepData.subtitle}
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-gray-300 mb-10 text-lg"
            >
              {currentStepData.description}
            </motion.p>
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg px-10 py-4 rounded-xl font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-shadow"
            >
              Let's Begin <ArrowRight className="inline ml-2 w-5 h-5" />
            </motion.button>
          </motion.div>
        )

      case 'question':
        return (
          <motion.div
            key={currentStepData.id}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 mb-4"
              >
                <Icon className="w-10 h-10 text-orange-400" />
              </motion.div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {currentStepData.title}
              </h2>
              <p className="text-orange-100 text-lg">
                {currentStepData.subtitle}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-8 mb-8">
              <p className="text-xl text-white mb-8 text-center font-medium">
                {currentStepData.question}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentStepData.options.map((option, index) => (
                  <motion.button
                    key={option.value}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(option.value)}
                    className={`option-card p-5 rounded-xl text-left transition-all ${
                      selectedOption === option.value
                        ? 'ring-2 ring-orange-500 bg-orange-500/20'
                        : 'hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">{option.emoji}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-white text-lg">
                          {option.label}
                        </p>
                        <p className="text-sm text-gray-400">
                          {option.description}
                        </p>
                      </div>
                      {selectedOption === option.value && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          <CheckCircle className="w-6 h-6 text-orange-500" />
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )

      case 'showcase':
        return (
          <motion.div
            key={currentStepData.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-10">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 mb-4"
              >
                <Icon className="w-10 h-10 text-orange-400" />
              </motion.div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {currentStepData.title}
              </h2>
              <p className="text-orange-100 text-lg">
                {currentStepData.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {currentStepData.features.map((feature, index) => {
                const FeatureIcon = feature.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.15 }}
                    whileHover={{ y: -4 }}
                    className="glass-card rounded-xl p-6"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <FeatureIcon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-300">
                      {feature.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>

            <div className="text-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNext}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg px-10 py-4 rounded-xl font-semibold shadow-lg shadow-orange-500/25"
              >
                Almost There <ArrowRight className="inline ml-2 w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        )

      case 'cta':
        return (
          <motion.div
            key={currentStepData.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-2xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="inline-flex p-8 rounded-3xl bg-gradient-to-br from-orange-500/20 to-red-500/20 mb-8 relative"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Icon className="w-20 h-20 text-orange-500" />
              </motion.div>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {currentStepData.title}
            </h2>
            <p className="text-xl text-orange-100 mb-4">
              {currentStepData.subtitle}
            </p>
            <p className="text-gray-300 mb-8 text-lg">
              {currentStepData.description}
            </p>

            <div className="glass-card rounded-xl p-6 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentStepData.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 text-left"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-white">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg px-10 py-4 rounded-xl font-semibold shadow-lg shadow-orange-500/25 mb-4 w-full sm:w-auto"
            >
              <Crown className="inline mr-2 w-5 h-5" />
              Create Free Account
            </motion.button>

            <p className="text-gray-400 text-sm mt-4">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/auth?mode=login')}
                className="text-orange-400 hover:text-orange-300 hover:underline"
              >
                Sign In
              </button>
            </p>
          </motion.div>
        )

      default:
        return null
    }
  }

  return (
    <div className="onboarding-screen min-h-screen flex flex-col">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-orange-950">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-900/20 via-transparent to-transparent" />
        <motion.div
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f97316" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        />
      </div>

      {/* Progress Bar with Steps */}
      <div className="fixed top-10 left-0 right-0 z-40">
        <div className="h-1 bg-gray-800">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-500 to-red-500"
            initial={{ width: '0%' }}
            animate={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        {/* Step Labels - Desktop only */}
        <div className="step-labels">
          {onboardingSteps.map((step, index) => (
            <div
              key={step.id}
              className={`step-label ${
                index === currentStep
                  ? 'active'
                  : index < currentStep
                  ? 'completed'
                  : 'pending'
              }`}
            >
              <span className="step-number">
                {index < currentStep ? '✓' : index + 1}
              </span>
              <span className="step-text">{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Skip Button */}
      <div className="fixed top-24 md:top-28 right-4 z-40">
        <button
          onClick={handleSkip}
          className="text-gray-400 hover:text-white transition-colors text-sm"
        >
          Skip →
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 pt-24 md:pt-28 relative z-10">
        <div className="w-full">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <div className="p-4 relative z-10">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          {currentStep > 0 ? (
            <motion.button
              whileHover={{ x: -4 }}
              onClick={handlePrev}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </motion.button>
          ) : (
            <div />
          )}

          {/* Step Dots (Mobile) */}
          <div className="flex gap-2 md:hidden">
            {onboardingSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-orange-500'
                    : index < currentStep
                    ? 'w-2 bg-orange-700'
                    : 'w-2 bg-gray-600'
                }`}
              />
            ))}
          </div>

          {currentStep < onboardingSteps.length - 1 && currentStepData.type !== 'question' ? (
            <motion.button
              whileHover={{ x: 4 }}
              onClick={handleNext}
              className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          ) : (
            <div />
          )}
        </div>
      </div>

      <style jsx>{`
        .glass-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .option-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .option-card:hover {
          border-color: rgba(249, 115, 22, 0.5);
        }

        .step-labels {
          display: none;
          justify-content: center;
          gap: 2rem;
          padding: 1rem 0;
          background: rgba(17, 24, 39, 0.8);
          backdrop-filter: blur(10px);
        }

        @media (min-width: 768px) {
          .step-labels {
            display: flex;
          }
        }

        .step-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .step-label.active {
          color: #f97316;
        }

        .step-label.completed {
          color: #d1d5db;
        }

        .step-label.pending {
          color: #9ca3af;
        }

        .step-number {
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
        }

        .step-label.active .step-number {
          background: #f97316;
          color: white;
        }

        .step-label.completed .step-number {
          background: #4b5563;
          color: white;
        }

        .step-label.pending .step-number {
          background: #1f2937;
          color: #6b7280;
        }

        .step-text {
          display: none;
        }

        @media (min-width: 1024px) {
          .step-text {
            display: inline;
          }
        }
      `}</style>
    </div>
  )
}

export default OnboardingScreen
